import {
  SelectorOptionsWithTooltip,
  initialTTSInstance,
  TTSInstance,
  TTSLanguage,
  PlayStatus,
  SelectedContourValueProps,
  defaultContourValues,
  defaultSampleRates,
  VoiceDescriptionProps,
  TTSDataInEnv,
} from "shared/models";
import { atom } from "jotai";
import {
  fetchTTS,
  fetchTTSOnPrem,
  fetchVoicesExtended,
  generateVoiceDescriptions,
  getLanguageNames,
  getLanguages,
  getAllUniqueLanguages,
  generateDynamicLanguageMapping,
} from "shared/utils/tts";
import { authTokenAtom, canLoadAppAtom } from "./auth";
import { fetchAccessibleEnvironments } from "shared/auth/app-access";
import { SSML_DOCS_DE_URL, SSML_DOCS_EN_URL } from "shared/constants";
import { fetchSSMLDocContent } from "shared/requests";
import { runtimeConfig } from "@/shared/config/runtime-config-loader";
import { ValidationError } from "@/shared/models/ssmlValidator";

/**
 * Primitives
 */

export const ttsAllDataAtom = atom<TTSDataInEnv[]>([]);
export const hasAccessToCurrentEnvAtom = atom<boolean>(false);

export const availableLanguagesAtom = atom<TTSLanguage[]>([]);
export const languageMappingAtom = atom<Record<string, string>>({});

// stores the data for TTS across all the accessible environments
export const isStopDisabledAtom = atom<boolean>(true);
export const shouldCloseMenuAtom = atom<boolean>(false);
export const deploymentOptionsAtom = atom<SelectorOptionsWithTooltip>([]);
export const sampleRateOptionsAtom = atom<SelectorOptionsWithTooltip>([]);
export const envOptionsAtom = atom<SelectorOptionsWithTooltip>([]);

export const ttsCustomEditorDirtyAtom = atom<boolean>(false);

// Atoms related to TTS Settings
export const selectedValuesAtom = atom<TTSInstance>(initialTTSInstance);
export const languageOptionsAtom = atom<SelectorOptionsWithTooltip>([]);
export const voiceOptionsAtom = atom<string[]>([]);

export const userInputAtom = atom<string>("");

// Atom to store different stages of play button since we need to fetch the selected text, if any from the editor
export const playStatusAtom = atom<PlayStatus>(PlayStatus.InActive);
// Atom to store the selected text so that only that can be sent to TTS to play
export const selectedTextForPlayAtom = atom<string>("");
// Atom to store the editor value
export const setEditorValueAtom = atom<string>("");

// Atom to store the current active ssml tags based on the selected text or cursor position
export const activeSSMLTagsAtom = atom<string[]>([]);

export const isHtmlViewAtom = atom<boolean>(false);

// Atom to indicate when to generate the phoneme for ipa and x-sampa by default
export const shouldGenerateIPAPhonemeAtom = atom<boolean>(false);
export const shouldGenerateSampaPhonemeAtom = atom<boolean>(false);

// Atom to check if the SSML editor is on/off
export const isSsmlViewEditable = atom<boolean>(false);

//Atom for style/emotions
export const styleEmotionNames = atom<string[]>([]);

// Atom to store the selected contour values from the graph
export const contourValuesAtom = atom<
  SelectedContourValueProps[],
  [SelectedContourValueProps[]],
  void
>(structuredClone(defaultContourValues), (_get, set, newValue) => {
  set(contourValuesAtom, newValue);
});

// Atom to store the voice description
export const voicesDescriptionAtom = atom<VoiceDescriptionProps[]>([]);

export const selectedSayAsLangAtom = atom<string>("");
export const selectedVoiceLangAtom = atom<string>("");

export const deSSMLDocAtom = atom<string>("");
export const enSSMLDocAtom = atom<string>("");

export const isAppLoadedAtom = atom<boolean>(false);

/**
 * Derived
 */

// Atom to set the default values of deployments and languages based on the selected environment.
export const setAllDefaultsAtom = atom(null, async (get, set, env: string) => {
  const token = runtimeConfig.ttsOnPrem ? "" : get(authTokenAtom);

  // update the deployments of the selected environment
  const ttsData = get(ttsAllDataAtom);
  const selectedValues = get(selectedValuesAtom);
  const envData = ttsData.filter((data) => data.envName === env);
  const deploymentNames: SelectorOptionsWithTooltip = [];
  let deploymentName = envData[0]?.data[0]?.name;
  let languages: TTSLanguage[] = [];
  let languageNames: SelectorOptionsWithTooltip = [];
  let selectedLanguage = "";

  const languageMapping = get(languageMappingAtom);

  envData[0]?.data.forEach((item) => {
    // if the new list of deployments contains the same deploymentName, then default to that
    if (item.name === selectedValues.selectedDeployment)
      deploymentName = item.name;
    // if the new languages contain the same selectedLanguage, then default to that
    languages = getLanguages(envData, deploymentName);
    if (
      languages.filter((l) => l.name === selectedValues.selectedLanguage)
        .length !== 0
    )
      selectedLanguage = selectedValues.selectedLanguage;
    else selectedLanguage = languages[0]?.name;
    languageNames = getLanguageNames(languages, languageMapping);

    deploymentNames.push({
      value: item.name,
      name: item.name,
      optionTooltip: item.description,
    });
  });

  // Set the default sample rate to the previously selected sample rate or "22050" on load
  const selectedSampleRate =
    selectedValues.selectedSampleRate || defaultSampleRates[0].value;

  // Set the default voice to the previously selected voice or "annie" on load
  const selectedVoice = selectedValues.selectedVoice || "annie";
  const listOfVoices = await fetchVoicesExtended(env, token);
  // generate the voice descriptions for catalog
  const voicesDescription = generateVoiceDescriptions(listOfVoices);

  set(selectedValuesAtom, {
    selectedEnv: env,
    selectedDeployment: deploymentName,
    selectedSampleRate,
    selectedLanguage,
    selectedVoice,
  });
  set(deploymentOptionsAtom, deploymentNames);
  set(languageOptionsAtom, languageNames);
  set(sampleRateOptionsAtom, defaultSampleRates);
  const totalVoices = voicesDescription.map((voice) => voice.voiceName);
  set(voiceOptionsAtom, totalVoices);
  set(voicesDescriptionAtom, voicesDescription);
});

export const handleAppLoadAtom = atom(null, async (get, set) => {
  const token = get(authTokenAtom);
  // Fetch the accessible environments
  const accessibleEnvs = fetchAccessibleEnvironments(token);

  // check if the user has access to the current env
  set(
    hasAccessToCurrentEnvAtom,
    accessibleEnvs.filter((env) => env.name === runtimeConfig.defaultEnv)
      .length > 0
  );

  // If accessible environments are none, then directly allow loading of app i.e AccessDenied page
  if (accessibleEnvs.length === 0) {
    set(canLoadAppAtom, true);
    return;
  }

  // Get services across all the accessible environments and update ttsAllData
  const ttsAllData: TTSDataInEnv[] = [];
  let envCount = 0;
  accessibleEnvs.forEach(async (env) => {
    const ttsServices = await fetchTTS(env.name, token);

    // set "default" as default deployment for TTS
    const defaultIndex = ttsServices.findIndex((s) => s.name === "default");
    if (defaultIndex !== -1) {
      const defaultService = ttsServices.splice(defaultIndex, 1)[0];
      ttsServices.splice(0, 0, defaultService);
    }

    // update appStore
    ttsAllData.push({
      envName: env.name,
      data: ttsServices,
    });
    envCount += 1;

    // update the ttsAllDataAtom once all the environments are looped thru
    if (envCount === accessibleEnvs.length) {
      set(ttsAllDataAtom, ttsAllData);

      // Generate dynamic language data from all fetched TTS services
      const allTTSServices = ttsAllData.flatMap((env) => env.data);

      const uniqueLanguages = getAllUniqueLanguages(allTTSServices);
      const dynamicMapping = generateDynamicLanguageMapping(allTTSServices);

      set(availableLanguagesAtom, uniqueLanguages);
      set(languageMappingAtom, dynamicMapping);

      // set the environment options
      const envs: SelectorOptionsWithTooltip = [];
      let hasAccessToProd = false;
      if (ttsAllData.length > 0) {
        ttsAllData.forEach((data) => {
          if (data.envName === (runtimeConfig.prodEnv || "production"))
            hasAccessToProd = true;
          envs.push({ value: data.envName, name: data.envName });
        });
        set(envOptionsAtom, envs);

        // set initial values
        const preferredEnv = hasAccessToProd
          ? runtimeConfig.prodEnv || "production"
          : runtimeConfig.defaultEnv || envs[0]?.name;
        set(setAllDefaultsAtom, preferredEnv ?? envs[0]?.name);
      }

      // Once processing is done, update canLoadAppAtom to true
      set(canLoadAppAtom, true);
    }
  });
});

export const handleAppLoadOnPremAtom = atom(null, async (_, set) => {
  const env = runtimeConfig.prodEnv || "production";
  const ttsServices = await fetchTTSOnPrem(env, "");
  // set "default" as default deployment for TTS
  const defaultIndex = ttsServices.findIndex((s) => s.name === "rtg");
  if (defaultIndex !== -1) {
    const defaultService = ttsServices.splice(defaultIndex, 1)[0];
    ttsServices.splice(0, 0, defaultService);
  }

  const formattedData = ttsServices.map(({ name, description, voices }) => {
    // Group voices by nativeLanguage
    const languageMap: Record<string, string[]> = {};
    voices.forEach((voice) => {
      if (!languageMap[voice.nativeLanguage]) {
        languageMap[voice.nativeLanguage] = [];
      }
      languageMap[voice.nativeLanguage].push(voice.name);
    });

    // Convert to TTSLanguage[]
    const languages: TTSLanguage[] = Object.entries(languageMap).map(
      ([langName, voiceNames]) => ({
        name: langName,
        voices: voiceNames,
      })
    );

    return {
      name,
      description,
      languages,
    };
  });

  set(ttsAllDataAtom, [
    {
      envName: env,
      data: formattedData,
    },
  ]);

  // Generate dynamic language data for on-prem
  const uniqueLanguages = getAllUniqueLanguages(formattedData);
  const dynamicMapping = generateDynamicLanguageMapping(formattedData);

  set(availableLanguagesAtom, uniqueLanguages);
  set(languageMappingAtom, dynamicMapping);

  set(envOptionsAtom, [{ value: env, name: env }]);
  set(setAllDefaultsAtom, env);
  set(hasAccessToCurrentEnvAtom, true);
  set(canLoadAppAtom, true);
  return;
});

export const updateContourValuesAtom = atom(
  null,
  async (get, set, { index, data }: SelectedContourValueProps) => {
    const contourValues = get(contourValuesAtom);
    const exists = contourValues.findIndex((c) => c.index === index);
    contourValues[exists] = {
      index,
      data: { time: data.time, value: data.value },
    };
    set(contourValuesAtom, contourValues);
  }
);

export const fetchSSMLDocsAtom = atom(null, async (get, set) => {
  const deSSMLDoc = get(deSSMLDocAtom);
  const enSSMLDoc = get(enSSMLDocAtom);
  if (!deSSMLDoc) {
    const deContent = await fetchSSMLDocContent(SSML_DOCS_DE_URL);
    set(deSSMLDocAtom, deContent);
  }
  if (!enSSMLDoc) {
    const enContent = await fetchSSMLDocContent(SSML_DOCS_EN_URL);
    set(enSSMLDocAtom, enContent);
  }
});

export const validationErrorsAtom = atom<ValidationError[]>([]);

